import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Routes } from "src/routes";
import { UserVerificationTypeEnum } from "src/types";
import { useUserVerification } from "src/verification";

export const useVerificationRedirect = () => {
    const { verificationType } = Routes.verifications.useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isVerificationDone, isVerificationLoading } = useUserVerification();

    const isSuccessPage = searchParams.get("success");
    const isCheckingPage = searchParams.get("checking");
    const projectSlug = searchParams.get("slug");

    const hasKyc = isVerificationDone(UserVerificationTypeEnum.KYC);
    const hasAml = isVerificationDone(UserVerificationTypeEnum.AML);
    const hasInvestorQuestionnaire = isVerificationDone(UserVerificationTypeEnum.INVESTOR_QUESTIONNAIRE);

    // The investor questionnaire page shows its own result screen (approved/rejected/warning) and
    // navigates onward itself once the user acts on it. If the questionnaire only just became
    // "done" during this visit (not when the page loaded), skip the automatic redirect below so it
    // doesn't yank the user away before they see that screen.
    const hadInvestorQuestionnaireOnLoad = useRef<boolean | null>(null);
    if (!isVerificationLoading && hadInvestorQuestionnaireOnLoad.current === null) {
        hadInvestorQuestionnaireOnLoad.current = hasInvestorQuestionnaire;
    }
    const justCompletedInvestorQuestionnaireHere =
        verificationType === UserVerificationTypeEnum.INVESTOR_QUESTIONNAIRE &&
        hadInvestorQuestionnaireOnLoad.current === false &&
        hasInvestorQuestionnaire;

    const redirect = useCallback(() => {
        if (justCompletedInvestorQuestionnaireHere) return;

        if (!hasKyc) {
            if (verificationType !== UserVerificationTypeEnum.KYC) {
                if (isCheckingPage) {
                    navigate(KYC_CHECKING_ROUTE);
                } else {
                    navigate(KYC_SELECTION_ROUTE);
                }
            }
            return;
        }
        if (!hasAml) {
            if (verificationType !== UserVerificationTypeEnum.AML) {
                navigate(Routes.verifications.fillPathParams({ verificationType: UserVerificationTypeEnum.AML }));
            }
            return;
        }
        if (!hasInvestorQuestionnaire) {
            if (verificationType !== UserVerificationTypeEnum.INVESTOR_QUESTIONNAIRE) {
                navigate(Routes.verifications.fillPathParams({ verificationType: UserVerificationTypeEnum.INVESTOR_QUESTIONNAIRE }));
            }
            return;
        }
        if (hasKyc && hasAml && hasInvestorQuestionnaire) {
            if (projectSlug) {
                navigate(Routes.projectOrder.fillPathParams({ projectSlug }));
            } else {
                navigate(Routes.projects.path);
            }
        }
    }, [
        hasAml,
        hasKyc,
        hasInvestorQuestionnaire,
        navigate,
        projectSlug,
        verificationType,
        isCheckingPage,
        justCompletedInvestorQuestionnaireHere,
    ]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;
        if (!isVerificationLoading) {
            const timeout = isSuccessPage ? 3000 : 0;
            if (timeout) {
                timeoutId = setTimeout(() => {
                    redirect();
                }, timeout);
            } else {
                redirect();
            }
        }

        // Cleanup function to clear the timeout if the component unmounts
        return () => clearTimeout(timeoutId);
    }, [isVerificationLoading, isSuccessPage, redirect]);
};

export const KYC_SELECTION_ROUTE = Routes.verifications.fillPathParams({ verificationType: UserVerificationTypeEnum.KYC });

export const KYC_CHECKING_ROUTE = `${KYC_SELECTION_ROUTE}?checking=true`;
